import React, { FC } from 'react';
import Head from 'next/head';

// Styles
import useTranslations, { RU_LANGUAGE, UK_LANGUAGE } from 'languages';
import { ArticleLink, Img, Title, TitleP, Wrap } from './style';

//hooks
import { useRouter } from 'next/router';
import { ArticleResponseType } from 'types/api/articleResponseType';
import { getPublicationDate } from 'utils/getPublicationDate';
import { useDefaultImage } from 'utils/hooks/useDefaultImage';
import { useLink } from 'utils/hooks/useLink';
import dynamic from 'next/dynamic';

interface PropsType {
  data?: ArticleResponseType;
  isShort?: boolean;
  isArticle?: boolean;
  isSpecialProgect?: boolean;
  one_post?: boolean;
  isOneImgUrl?: boolean;
  isRightSideBarIn?: boolean;
  isLazyLoad?: boolean;
  isPriority?: boolean;
  sizes?: string;
  imgFill?: boolean;
}

const CategoryTitle = dynamic(
  () => import('modules/main/components/NewCard/components/CategoryTitle'),
  {
    ssr: false,
  }
);

const StubForUntranslatedArticles = dynamic(
  () => import('modules/main/components/NewCard/components/StubForUntranslatedArticles'),
  {
    ssr: false,
  }
);

const ShortNewCard = dynamic(
  () => import('modules/main/components/NewCard/components/ShortNewCard'),
  {
    ssr: false,
  }
);

export const NewCard: FC<PropsType> = ({
  data,
  isShort = false,
  isArticle,
  isSpecialProgect,
  one_post,
  isOneImgUrl,
  isRightSideBarIn = false,
  isLazyLoad,
  isPriority,
  sizes,
  imgFill,
  ...props
}) => {
  const {
    image = '',
    s3 = '',
    publication_date = '',
    translationData,
    seo = {},
    categories = [],
    categoriesByLang,
    id,
  } = { ...data };

  // Only date without time
  const publicationDateNoTime = getPublicationDate(publication_date);

  const { title = '', sub_title = '', image: imageMain = '' } = { ...translationData };

  const { locale = '' } = useRouter();

  const articleEnv = useLink();

  const { slug_uk = '', slug_ru = '', slug_en = '' } = seo;

  const slugDiferLang =
    locale === UK_LANGUAGE ? slug_uk : locale === RU_LANGUAGE ? slug_ru : slug_en;

  const translationForSpecialProgect = isSpecialProgect ? title : slugDiferLang;

  const linkForSpecialProgect =
    isSpecialProgect && slugDiferLang
      ? `${articleEnv}/special_progect/${slugDiferLang}`
      : `${articleEnv}/${slugDiferLang}`;

  const t = useTranslations();

  const { defaultImage = '' } = useDefaultImage(
    image || '',
    s3 || '',
    imageMain || '',
    undefined,
    undefined
  );

  return (
    <Wrap {...props} key={`${id}`}>
      {isPriority && (
        <Head>
          <link rel="preload" href={defaultImage} as="image" />
        </Head>
      )}
      {translationForSpecialProgect !== '' && (
        <>
          <ArticleLink href={linkForSpecialProgect} className="one_post">
            <Img
              src={defaultImage}
              className={`${imgFill ? 'imgFill' : ''} lazyload`}
              loading={isLazyLoad && 'lazy'}
              priority={isPriority}
              alt="article image"
              quality={80}
              sizes={sizes}
            />
            <CategoryTitle categories={categories} />
          </ArticleLink>
          {isRightSideBarIn ? (
            <ArticleLink href={linkForSpecialProgect}>
              {isArticle ? (
                <TitleP>{data?.title}</TitleP>
              ) : (
                <TitleP>{data?.title}</TitleP>
              )}
            </ArticleLink>
          ) : (
            <ArticleLink href={linkForSpecialProgect}>
              {isArticle ? <Title>{title}</Title> : <Title>{title}</Title>}
            </ArticleLink>
          )}
          {!isShort && (
            <ShortNewCard
              sub_title={sub_title}
              data_sub_title={data?.sub_title || ''}
              categoriesByLang={categoriesByLang}
              publicationDateNoTime={publicationDateNoTime}
              isArticle={isArticle}
              articleEnv={articleEnv}
            />
          )}
        </>
      )}
      {translationForSpecialProgect === '' && (
        <StubForUntranslatedArticles
          defaultImage={defaultImage}
          isSpecialProgect={isSpecialProgect}
          seo={seo}
          categories={categories}
          sizes={sizes}
          isPriority={isPriority}
          isLazyLoad={isLazyLoad}
        />
      )}
    </Wrap>
  );
};

export default NewCard;
